import { type AxiosResponse } from 'axios';
import { getCookie } from 'commercial-web-client-utilities/dist/cookie';
import type { LedsService } from 'commercial-web-client-utilities/dist/composables/leds-service';
import { NonsecureService } from 'commercial-web-client-utilities/dist/composables/nonsecure-service';

import { GetUsernameResponse, SecureLinkResponse } from '@/types/forgot-username';

interface CreateMemberProfileRecoveryServiceOptions {
    ledsService: LedsService;
    nonsecureService: NonsecureService;
}

export interface AuthenticateOptions {
    signal?: AbortSignal;
}

export interface UserNameOptions {
    signal?: AbortSignal;
    recoveryIdentifier: string;
}

export interface SecureLinkOptions {
    withIdCardNumber?: {
        idCardNumber: string,
        firstName?: string,
        dateOfBirth: string
    },
    withSocialSecurityNumber?: {
        socialSecurityNumber: string,
        firstName?: string,
        dateOfBirth: string
    },
    withUsername?: {
        username: string,
        dateOfBirth: string
  },
}

export interface MemberProfileRecoveryService {
    secureLink(options: SecureLinkOptions):
        Promise<AxiosResponse<SecureLinkResponse>>;
    getUsername(payload: unknown):
        Promise<AxiosResponse<GetUsernameResponse>>;
}

/**
 * Creates the Profile Recovery Service
 * @param serviceOptions {CreateMemberProfileRecoveryServiceOptions}
 * @returns MemberProfileRecoveryService
 */
export function createMemberProfileRecoveryService(
    serviceOptions: CreateMemberProfileRecoveryServiceOptions
): MemberProfileRecoveryService {
    const {
        ledsService,
        nonsecureService,
    } = serviceOptions;

    const nonsecureClient = nonsecureService.client;
    const ledsClient = ledsService.client;
    const baseUrl = ledsClient.defaults.baseURL;
    const baseProfileRecoveryUrl = `${baseUrl}member/profile-recovery`;
    const region = getCookie('selectedRegion');

    async function secureLink(
        options: SecureLinkOptions
    ): Promise<AxiosResponse<SecureLinkResponse>> {
        if (!nonsecureClient) throw new Error('Nonsecure client required.');

        const headers = await ledsService.createAuthorizationHeaders();
        if (region) headers['X-Region'] = region;
        const url = `${baseProfileRecoveryUrl}/secureLink`;

        return await nonsecureClient.post(url, { ...options, generateOneTimePassword: false }, { headers });
    }

    async function getUsername(options: UserNameOptions): Promise<AxiosResponse<GetUsernameResponse>> {
        if (!nonsecureClient) throw new Error('Nonsecure client required.');
        const signal = options.signal;
        const headers = await ledsService.createAuthorizationHeaders();
        if (region) headers['X-Region'] = region;
        if (options.recoveryIdentifier) {
            headers.Authorization = options.recoveryIdentifier;
        }
        const url = `${baseProfileRecoveryUrl}/username`;

        return await nonsecureClient.get(url, { headers, signal });
    }

    return {
        secureLink,
        getUsername
    };
}
